import React from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis
} from 'recharts';
import { ChartTooltipCard, ChartTooltipRow, EmptyState, Panel } from './Panel';
import { CumulativePoint } from './metrics';
import { Bucket, formatBucketLabel, formatBucketTick } from './period';
import { accent, chart, formatCount, series } from './tokens';

interface CumulativeStampsChartProps {
  data: CumulativePoint[];
  bucket: Bucket;
  rangeLabel: string;
}

/**
 * Lifetime stamp total over the visible window — the line a merchant watches to
 * see whether the programme is still compounding.
 */
export const CumulativeStampsChart: React.FC<CumulativeStampsChartProps> = ({
  data,
  bucket,
  rangeLabel
}) => {
  const last = data[data.length - 1]?.total ?? 0;
  const first = data[0]?.total ?? 0;
  const hasData = last > 0;

  const renderTooltip = ({ active, label }: TooltipProps<number, string>) => {
    if (!active) return null;
    const point = data.find((entry) => entry.key === label);
    if (!point) return null;
    return (
      <ChartTooltipCard title={formatBucketLabel(point.key, bucket)}>
        <ChartTooltipRow
          color={series.stamps}
          label='Stamps all time'
          value={formatCount(point.total)}
        />
      </ChartTooltipCard>
    );
  };

  return (
    <Panel
      title='Lifetime stamps'
      subtitle={
        hasData
          ? `${formatCount(last)} all time · ${formatCount(last - first)} added in ${rangeLabel}`
          : rangeLabel
      }
      hint='The location’s running stamp total. It counts every stamp ever issued, so the line never restarts at zero when you change the period.'
    >
      {!hasData ? (
        <EmptyState
          height={chart.height}
          message='No stamps recorded yet. This line starts climbing with your first scan.'
        />
      ) : (
        <ResponsiveContainer width='100%' height={chart.height}>
          <AreaChart data={data} margin={chart.margin}>
            <defs>
              <linearGradient id='cumulative-fill' x1='0' y1='0' x2='0' y2='1'>
                <stop offset='0%' stopColor={accent.mid} stopOpacity={0.24} />
                <stop offset='100%' stopColor={accent.mid} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} stroke={chart.gridStroke} />
            <XAxis
              dataKey='key'
              tickFormatter={(key: string) => formatBucketTick(key, bucket)}
              tick={chart.tick}
              tickLine={false}
              axisLine={{ stroke: chart.gridStroke }}
              minTickGap={28}
            />
            <YAxis
              tick={chart.tick}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
              width={48}
            />
            <Tooltip content={renderTooltip} cursor={{ stroke: accent.mid, strokeWidth: 1 }} />
            <Area
              type='monotone'
              dataKey='total'
              stroke={accent.mid}
              strokeWidth={2.25}
              fill='url(#cumulative-fill)'
              activeDot={{ r: 4, strokeWidth: 0 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </Panel>
  );
};

export default CumulativeStampsChart;
